import { useState, useEffect, useRef, useCallback } from 'react';

export const useWebSocket = (url) => {
  const [lastMessage, setLastMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10; // Increased for mobile
  const heartbeatIntervalRef = useRef(null);
  const lastHeartbeatRef = useRef(Date.now());
  const isMobile = useRef(false);

  // Detect mobile device
  useEffect(() => {
    isMobile.current = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    console.log('Device detected as mobile:', isMobile.current);
  }, []);

  // Heartbeat to keep connection alive (especially important for mobile)
  const startHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
    }
    
    heartbeatIntervalRef.current = setInterval(() => {
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        try {
          wsRef.current.send(JSON.stringify({ type: 'ping' }));
          lastHeartbeatRef.current = Date.now();
        } catch (error) {
          console.warn('Heartbeat failed:', error);
        }
      }
    }, 30000); // Send ping every 30 seconds
  }, []);

  const stopHeartbeat = useCallback(() => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  }, []);

  // Network change detection for mobile
  useEffect(() => {
    if (!isMobile.current) return;

    const handleOnline = () => {
      console.log('Network came online, attempting to reconnect...');
      if (connectionStatus === 'disconnected') {
        reconnectAttempts.current = 0;
        connect();
      }
    };

    const handleOffline = () => {
      console.log('Network went offline');
      setConnectionStatus('disconnected');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [connectionStatus]);

  // Visibility change handling for mobile (app going to background)
  useEffect(() => {
    if (!isMobile.current) return;

    const handleVisibilityChange = () => {
      if (document.hidden) {
        console.log('App went to background');
        // Don't disconnect, but be ready for reconnection
      } else {
        console.log('App came to foreground');
        // Check if connection is still alive
        if (wsRef.current && wsRef.current.readyState !== WebSocket.OPEN) {
          console.log('Connection lost while in background, reconnecting...');
          reconnectAttempts.current = 0;
          connect();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const connect = useCallback(() => {
    if (!url) return;

    try {
      // Close existing connection if any
      if (wsRef.current) {
        wsRef.current.close();
      }

      console.log('Attempting to connect to:', url);
      setConnectionStatus('connecting');
      
      wsRef.current = new WebSocket(url);
      
      // Set connection timeout for mobile
      const connectionTimeout = setTimeout(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.CONNECTING) {
          console.warn('Connection timeout, closing and retrying...');
          wsRef.current.close();
        }
      }, isMobile.current ? 15000 : 10000); // Longer timeout for mobile
      
      wsRef.current.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connected to:', url);
        setConnectionStatus('connected');
        reconnectAttempts.current = 0;
        startHeartbeat();
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('WebSocket message received:', data);
          
          // Handle ping/pong for connection health
          if (data.type === 'pong') {
            lastHeartbeatRef.current = Date.now();
            return;
          }
          
          setLastMessage(event);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error);
        }
      };

      wsRef.current.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket disconnected:', event.code, event.reason);
        setConnectionStatus('disconnected');
        stopHeartbeat();
        
        // More aggressive reconnection for mobile
        if (event.code !== 1000 && reconnectAttempts.current < maxReconnectAttempts) {
          reconnectAttempts.current += 1;
          const baseDelay = isMobile.current ? 2000 : 1000;
          const delay = Math.min(baseDelay * Math.pow(2, reconnectAttempts.current - 1), 30000);
          
          console.log(`Scheduling reconnect attempt ${reconnectAttempts.current}/${maxReconnectAttempts} in ${delay}ms`);
          
          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(`Attempting to reconnect (${reconnectAttempts.current}/${maxReconnectAttempts})`);
            setConnectionStatus('connecting');
            connect();
          }, delay);
        }
      };

      wsRef.current.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error:', error);
        setConnectionStatus('disconnected');
      };

    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
      setConnectionStatus('disconnected');
    }
  }, [url, startHeartbeat, stopHeartbeat]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    stopHeartbeat();
    
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    
    setConnectionStatus('disconnected');
    reconnectAttempts.current = 0;
  }, [stopHeartbeat]);

  const sendMessage = useCallback((message) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try {
        console.log('Sending WebSocket message:', message);
        wsRef.current.send(JSON.stringify(message));
      } catch (error) {
        console.error('Failed to send message:', error);
        // If send fails, try to reconnect
        if (reconnectAttempts.current < maxReconnectAttempts) {
          console.log('Send failed, attempting to reconnect...');
          reconnectAttempts.current = 0;
          connect();
        }
      }
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
      // Queue message for retry on mobile
      if (isMobile.current && reconnectAttempts.current < maxReconnectAttempts) {
        console.log('Queueing message for retry on mobile...');
        // Could implement message queuing here if needed
      }
    }
  }, [connect]);

  // Connect when URL changes
  useEffect(() => {
    console.log('WebSocket URL changed to:', url);
    if (url) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [url, connect, disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    sendMessage,
    lastMessage,
    connectionStatus,
    connect,
    disconnect
  };
}; 